import { FC, useContext } from "react";
import { Heading } from "@otrium/atoms";
import { Trans } from "@lingui/macro";
import useAlgoliaProductsCarousel from "src/hooks/useAlgoliaProductsCarousel";
import { CarouselType } from "src/types/AlgoliaProductsCarousel.d";
import { AlgoliaIconicCarousel } from "src/molecules/AlgoliaIconicCarousel";
import {
  Content,
  IconicCarouselContainer,
} from "src/modules/brands/BrandView.styled";
import { PLPType } from "src/types/PLPType.d";
import useFilterProductLabels from "src/hooks/useFilterProductLabels";
import { PageContentType } from "src/types/graphql.d";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { AppCtx } from "src/contexts/app.context";
import { ContentMobileFullscreen } from "src/atoms/Content";
import classNames from "classnames";
import { SortBy } from "src/types/Algolia.d";

interface AlgoliaIconicProductsProps {
  genderId: string;
  localeGenderId: string;
  slug: string;
  plpType?: PLPType;
  noPadding?: boolean;
}

const HIDDEN_BRAND_SLUG = ["scotch-and-soda"];

const AlgoliaIconicProducts: FC<AlgoliaIconicProductsProps> = ({
  genderId,
  slug,
  localeGenderId,
  plpType,
  noPadding,
}) => {
  const { products } = useAlgoliaProductsCarousel({
    carouselType: CarouselType.ICONS,
    gender: localeGenderId,
    brandSlug: slug,
    labelsType: "icons",
    ruleContexts: ["brand_page_icons_carousel"],
    sortBy: SortBy.NEW,
  });
  const { locale } = useContext(AppCtx);

  const { data: labels } = useFilterProductLabels({
    products: products?.results || [],
    pageContentType:
      plpType === PLPType.BRAND_PLP
        ? PageContentType.Brand
        : PageContentType.Category,
  });

  if (
    !products?.results ||
    products.results.length < 4 ||
    HIDDEN_BRAND_SLUG.includes(slug)
  ) {
    return null;
  }

  const viewAllLink =
    products.results?.length > 12
      ? `${getI18nRoute({
          route: `/brand`,
          locale,
        })}/${slug}/icons/${genderId}`
      : undefined;

  return (
    <ContentMobileFullscreen
      sx={{ width: "100%" }}
      className={classNames({ ["no-padding"]: noPadding })}
    >
      <IconicCarouselContainer data-testid="icons-carousel">
        <AlgoliaIconicCarousel
          genderId={genderId}
          localeGenderId={localeGenderId}
          title="Icons"
          products={products.results}
          labels={labels}
          maxItems={12}
          headerLinkHref={viewAllLink}
          slidesDesktop={6}
          slidesTablet={4}
          slidesMobile={2.5}
        />
      </IconicCarouselContainer>
      {products.results && products.results.length > 3 && (
        <Content>
          <Heading
            as="h2"
            variant="h2"
            fontWeight="700"
            fontSize={[3, 3, 3, 3, 4]}
            mt={[3, 3, 3, 5, 5]}
            sx={{ textTransform: "none" }}
          >
            <Trans>All products</Trans>
          </Heading>
        </Content>
      )}
    </ContentMobileFullscreen>
  );
};

export { AlgoliaIconicProducts };
